// CANONICAL report feedback: toast notifications and the generating overlay.
//
// This file is the single source of truth. It is copied verbatim into the Admin
// and Teacher panels by `Scripts/sync_report_renderers.py`; do not edit the
// panel copies.
//
// Report generation takes a read-only snapshot across several source
// collections, so it is slow enough that the user needs to see it working and
// be told plainly when it succeeded.
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, CheckCircle2, Loader2, X } from "lucide-react";

const TONES = {
  success: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-800", icon: "text-emerald-600" },
  error: { border: "border-red-200", bg: "bg-red-50", text: "text-red-800", icon: "text-red-600" },
};

/** A single auto-dismissing toast, pinned top-right above the page. */
export function Toast({ notice, onDismiss, duration = 5000 }) {
  useEffect(() => {
    if (!notice || notice.type === "error") return undefined;
    const timer = setTimeout(onDismiss, duration);
    return () => clearTimeout(timer);
  }, [notice, onDismiss, duration]);

  if (!notice) return null;
  const tone = TONES[notice.type] || TONES.success;
  const Icon = notice.type === "error" ? AlertCircle : CheckCircle2;

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 top-5 z-[120] flex justify-center px-4 font-[Poppins] sm:justify-end sm:pr-6">
      <div
        role="status"
        aria-live="polite"
        className={`pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border ${tone.border} ${tone.bg} p-4 shadow-lg`}
      >
        <Icon size={20} className={`mt-0.5 shrink-0 ${tone.icon}`} />
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${tone.text}`}>{notice.title || (notice.type === "error" ? "Something went wrong" : "Done")}</p>
          <p className={`mt-0.5 text-sm ${tone.text} opacity-90`}>{notice.text}</p>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss notification" className={`shrink-0 rounded-lg p-1 ${tone.text} opacity-60 transition hover:opacity-100`}>
          <X size={16} />
        </button>
      </div>
    </div>,
    document.body
  );
}

/** Blocking overlay shown while a report snapshot is being generated. */
export function GeneratingOverlay({ open, title = "Generating report", text = "Collecting verified source records and preparing the snapshot. This can take a few moments." }) {
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/45 px-4 font-[Poppins]">
      <div role="alertdialog" aria-busy="true" aria-label={title} className="w-full max-w-sm rounded-2xl bg-white p-7 text-center shadow-2xl">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orange-50">
          <Loader2 size={28} className="animate-spin text-[#D67C2C]" />
        </div>
        <h2 className="mt-4 text-lg font-bold text-slate-900">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
      </div>
    </div>,
    document.body
  );
}

/** Inline spinner for a button that is mid-action. */
export function ButtonSpinner({ size = 17 }) {
  return <Loader2 size={size} className="animate-spin" />;
}
